// Path: src/components/auth/register/Register.tsx
// Removed FullHeightLayout, BreadcrumbWrapper, and Footer to prevent duplicate menu
// Updated Breadcrumb to use items prop per Ant Design deprecation warning
// 05/31/25 05:15 PM +07
import React, { useState, useEffect } from "react";
import { Button, Card, Radio, Typography, Breadcrumb } from "antd";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import styled from "styled-components";
import { useUserStore } from "../../../stores/UserStore";
import Spinner from "../../dev/utils/Spinner/Spinner";
import { Helmet } from "react-helmet-async";
import { withFirebase, WithFirebaseProps } from "../../dev/Firebase/Firebase";
import "./Register.css";
import { UserOutlined, GlobalOutlined, DollarOutlined, HomeOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const StyledRadio = styled(Radio)`
  display: flex !important;
  align-items: center;
  height: 40px;
  line-height: 40px !important;
  color: #0427f4;
  font-size: 18px;
  margin-bottom: 20px;
  transition: background-color 0.3s ease, transform 0.3s ease;

  .ant-radio + * {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &:hover {
    background: rgba(4, 39, 244, 0.1);
    transform: translateX(5px);
  }

  .ant-radio-checked + * {
    font-weight: 600;
    color: #0427f4;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 15px;
    padding: 5px 0;
    height: 32px;
    line-height: 32px !important;
  }
`;

const CenteredContent = styled.div`
  padding: 0 !important;
  text-align: center;
  flex: 1;
  box-sizing: border-box;
  width: 100% !important;
  margin: 0 !important;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0 !important;
    width: 100% !important;
  }
`;

const StyledCard = styled(Card)`
  width: 100% !important;
  max-width: 800px;
  margin: 0 auto !important;
  background: #fce248;
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.4);
  border: 1px solid #ffeb3b;
  padding: 40px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 30px;

  & > div,
  .ant-typography,
  .radio-group,
  .ant-btn {
    background: transparent !important;
  }

  @media (max-width: 768px) {
    width: 92% !important;
    padding: 25px;
    margin: 0 auto !important;
    img {
      width: 100px;
      height: 60px;
    }
    .ant-typography {
      font-size: 12px !important;
      margin-bottom: 4px;
    }
    .radio-group {
      width: 100%;
      text-align: left;
      margin-bottom: 4px;
    }
    .ant-btn {
      margin: 8px auto;
      padding: 12px 20px;
      font-size: 14px !important;
      width: 35%;
      max-width: 160px;
      display: inline-block;
      text-align: center;
    }
  }
`;

const BlueButton = styled(Button)`
  background: #0427f4 !important;
  color: #fff !important;
  margin: 8px auto !important;
  border-radius: 8px !important;
  padding: 12px 20px !important;
  border: none !important;
  width: 35% !important;
  max-width: 160px !important;
  display: inline-block !important;
  text-align: center !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &.ant-btn {
    background: #0427f4 !important;
    color: #fff !important;
    border: none !important;
  }

  &:hover,
  &:focus,
  &:active {
    background: #0427f4 !important;
    color: #fff !important;
    opacity: 0.9;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 45% !important;
    max-width: 140px !important;
    padding: 10px 15px !important;
  }
`;

const BackButton = styled(Button)`
  background: transparent !important;
  color: #0427f4 !important;
  border: 1px solid #0427f4 !important;
  margin: 8px auto !important;
  border-radius: 8px !important;
  padding: 12px 20px !important;
  width: 35% !important;
  max-width: 160px !important;
  display: inline-block !important;
  text-align: center !important;
  transition: all 0.3s ease;

  &:hover,
  &:focus,
  &:active {
    background: #0427f4 !important;
    color: #fff !important;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 45% !important;
    max-width: 140px !important;
    padding: 10px 15px !important;
  }
`;

const BreadcrumbWrapper = styled.div`
  margin: 20px 0;
  text-align: left;
  width: 100%;
  max-width: 800px;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    margin: 10px 0;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const Register: React.FC<WithFirebaseProps> = ({ firebase }) => {
  const [formType, setFormType] = useState<string | null>(null);
  const userStore = useUserStore();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("Register.tsx: userStore.isLoaded:", userStore.isLoaded);
    console.log("Register.tsx: userStore.isLoggedIn:", userStore.isLoggedIn);
    if (!userStore.isLoaded) return;
    if (userStore.isLoggedIn && firebase.auth.currentUser?.emailVerified) {
      navigate("/verification");
    }
  }, [userStore.isLoaded, userStore.isLoggedIn, navigate, firebase.auth]);

  const onChange = (e: RadioChangeEvent) => {
    const value = e.target.value as string;
    console.log("Selected formType:", value);
    setFormType(value);
    if (value) {
      navigate(`/register/${value}`, { replace: true });
    }
  };

  const handleNext = () => {
    console.log("handleNext called, formType:", formType);
    if (formType) {
      navigate(`/register/${formType}`, { replace: true });
    } else {
      console.log("No formType selected, navigation aborted");
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (!userStore.isLoaded) {
    return <Spinner />;
  }

  return (
    <>
      <Helmet>
        <title>Register for onelightsystem OLS | Sun Light Meditation Training</title>
        <meta
          name="description"
          content="Sign up now for onelightsystem OLS to access Sun Light Meditation training, OLSME subscriptions, and OLSLM assets. Join our Light Civilization community today!"
        />
        <meta
          name="keywords"
          content="OLS registration, Sun Light Meditation, join OLS, OLSME subscription, OLSLM assets, meditation courses, spiritual growth, onelightsystem OLS"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Register for onelightsystem OLS | Sun Light Meditation Training" />
        <meta
          property="og:description"
          content="Sign up now for onelightsystem OLS to access Sun Light Meditation training, OLSME subscriptions, and OLSLM assets. Join our Light Civilization community today!"
        />
        <meta property="og:url" content="https://www.olsme.com/register" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for Registration Page" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="Register for onelightsystem OLS | Sun Light Meditation Training" />
        <meta
          name="twitter:description"
          content="Sign up now for onelightsystem OLS to access Sun Light Meditation training, OLSME subscriptions, and OLSLM assets. Join our Light Civilization community today!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for Registration Page" />
        <link rel="canonical" href="https://www.olsme.com/register" />
        <link rel="preload" href="/logo.jpg" as="image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Register for onelightsystem OLS",
            "url": "https://www.olsme.com/register",
            "description": "Sign up for onelightsystem OLS to join Sun Light Meditation training programs, OLSME subscriptions, and OLSLM asset management, fostering a Light Civilization community.",
            "mainEntity": {
              "@type": "Service",
              "name": "OLS Registration",
              "description": "Registration service for onelightsystem OLS, offering Sun Light Meditation training, OLSME subscriptions, and OLSLM asset management.",
              "provider": {
                "@type": "Organization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com",
                "logo": "https://www.olsme.com/logo.jpg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "info@olsme.com",
                  "telephone": "+1-312-945-6172",
                },
                "sameAs": [
                  "https://www.linkedin.com/company/onelightsystem",
                  "https://twitter.com/onelightsystem",
                  "https://www.facebook.com/onelightsystem",
                  "https://www.instagram.com/onelightsystem",
                  "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                ],
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "logo": "https://www.olsme.com/logo.jpg",
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "OLS Student Registration",
                "description": "Introductory and initiation evaluation for Sun Light Meditation training.",
                "url": "https://www.olsme.com/register/olssregister",
                "availability": "https://schema.org/InStock",
              },
              {
                "@type": "Offer",
                "name": "OLSME Subscription",
                "description": "Public and corporate packages for meditation training and energy alignment.",
                "url": "https://www.olsme.com/register/olsmeregister",
                "availability": "https://schema.org/InStock",
              },
              {
                "@type": "Offer",
                "name": "OLSLM Assets",
                "description": "Real asset value management through Sun Light Meditation practices.",
                "url": "https://www.olsme.com/register/olslmregister",
                "availability": "https://schema.org/InStock",
              },
            ],
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Register",
                  "item": "https://www.olsme.com/register",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <RouterLink to="/">
                  <HomeOutlined /> Home
                </RouterLink>
              ),
            },
            { title: "Register" },
          ]}
        />
      </BreadcrumbWrapper>
      <CenteredContent>
        <StyledCard>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <a href="/">
              <img
                style={{ width: "120px", height: "70px" }}
                width="120"
                height="70"
                alt="onelightsystem OLS Logo for Registration Page"
                src="/logo.jpg"
                loading="lazy"
              />
            </a>
          </div>
          <Title level={1} style={{ color: "#0427f4", marginBottom: "8px", marginTop: "20px", fontSize: "24px" }}>
            Join onelightsystem OLS
          </Title>
          <Text style={{ color: "#0427f4", marginBottom: "20px", display: "block" }}>
            Select your registration type to begin your journey
          </Text>
          {userStore.isLoggedIn && (
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Text strong style={{ color: "#0427f4" }}>
                Current role: {userStore.userRole || "UNAUTHORIZED"}
              </Text>
            </div>
          )}
          <div className="radio-group">
            <Radio.Group onChange={onChange} value={formType} aria-label="Registration type selection">
              <StyledRadio value="olssregister">
                <UserOutlined style={{ fontSize: "18px", color: "#0427f4" }} />
                OLS Student (Intro & Initiation Training)
              </StyledRadio>
              <StyledRadio value="olsmeregister">
                <GlobalOutlined style={{ fontSize: "18px", color: "#0427f4" }} />
                OLSME Subscription (Public/Corporate Packages)
              </StyledRadio>
              <StyledRadio value="olslmregister">
                <DollarOutlined style={{ fontSize: "18px", color: "#0427f4" }} />
                OLSLM Assets (Real Asset Management)
              </StyledRadio>
            </Radio.Group>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
            <BlueButton size="large" onClick={handleNext} disabled={!formType}>
              Next
            </BlueButton>
          </div>
          <Text style={{ color: "#0427f4", marginTop: "20px", display: "block" }}>
            <RouterLink to="/OLSstudentProspect" style={{ color: "#0427f4", textDecoration: "underline" }}>
              OLSStudentProspect
            </RouterLink>{" "}
            ,{" "}
            <RouterLink to="/olsme" style={{ color: "#0427f4", textDecoration: "underline" }}>
              OLSME
            </RouterLink>{" "}
            ,{" "}
            <RouterLink to="/olslm" style={{ color: "#0427f4", textDecoration: "underline" }}>
              OLSLM Assets
            </RouterLink>
          </Text>
        </StyledCard>
      </CenteredContent>
    </>
  );
};

export default withFirebase(Register);